import { useState, useCallback, useEffect, useRef } from 'react'
import { useSSE } from './useSSE'
import { getProcessingJob } from '@/api/processing'
import type { SSEEvent, ProgressSSEMessage } from './useSSE'
import type { ProcessingResponse } from '@/types'

export type ProcessingState = 'idle' | 'processing' | 'complete' | 'error'

export interface ProcessingStatus {
  state: ProcessingState
  progress: number
  stage: string
  errorMessage: string | null
  resourceId: string | null
  reset: () => void
}

const POLL_INTERVAL = 5000

export function useProcessingStatus(
  processingResponse: ProcessingResponse | null
): ProcessingStatus {
  const [state, setState] = useState<ProcessingState>('idle')
  const [progress, setProgress] = useState(0)
  const [stage, setStage] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [sseEnabled, setSseEnabled] = useState(true)
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stateRef = useRef(state)
  stateRef.current = state

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const handleMessage = useCallback((event: SSEEvent) => {
    const data = event.data as ProgressSSEMessage

    if (event.type === 'progress' || data.status === 'PROCESSING') {
      setState('processing')
      if (data.percent != null) setProgress(data.percent)
      if (data.stage) setStage(data.stage)
    }
  }, [])

  const handleComplete = useCallback(
    (event: SSEEvent) => {
      const data = event.data as ProgressSSEMessage
      stopPolling()

      if (data.status === 'FAILED' || data.error) {
        setState('error')
        setErrorMessage(data.error ?? data.message ?? 'Processing failed')
      } else {
        setState('complete')
        setProgress(100)
        setStage('Complete')
      }
    },
    [stopPolling]
  )

  const handleSSEError = useCallback(() => {
    setSseEnabled(false)
  }, [])

  const reset = useCallback(() => {
    setState('idle')
    setProgress(0)
    setStage('')
    setErrorMessage(null)
    setSseEnabled(true)
    stopPolling()
  }, [stopPolling])

  const url = processingResponse?.events_url ?? null
  const sseActive =
    !!processingResponse &&
    sseEnabled &&
    state !== 'complete' &&
    state !== 'error'

  if (processingResponse && state === 'idle') {
    setState('processing')
  }

  useSSE({
    url,
    onMessage: handleMessage,
    onComplete: handleComplete,
    onError: handleSSEError,
    enabled: sseActive,
  })

  // Polling fallback when SSE fails
  const processingId = processingResponse?.processing_id ?? null
  useEffect(() => {
    if (!processingId) {
      stopPolling()
      return
    }

    // Check terminal state via ref to avoid re-running effect on state changes
    if (stateRef.current === 'complete' || stateRef.current === 'error') {
      return
    }

    const poll = async () => {
      // Skip if already terminal
      if (stateRef.current === 'complete' || stateRef.current === 'error') {
        stopPolling()
        return
      }

      try {
        const job = await getProcessingJob(processingId)

        if (job.status === 'COMPLETED') {
          setState('complete')
          setProgress(100)
          setStage('Complete')
          stopPolling()
        } else if (job.status === 'FAILED') {
          setState('error')
          setErrorMessage(job.error_message ?? 'Processing failed')
          stopPolling()
        } else {
          setState('processing')
          setProgress(prev =>
            prev === job.progress_percent ? prev : job.progress_percent
          )
          setStage(prev => {
            const next = job.current_stage ?? prev
            return prev === next ? prev : next
          })
        }
      } catch {
        // Polling request failed — keep trying
      }
    }

    poll()
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL)

    return () => stopPolling()
  }, [processingId, stopPolling])

  return {
    state,
    progress,
    stage,
    errorMessage,
    resourceId: processingResponse?.resource_id ?? null,
    reset,
  }
}
